<script lang="ts">
    import { getContext, onDestroy } from 'svelte'
    import { derived as sderived } from 'svelte/store'

    import { gettext as _ } from 'hydria/services/gettext'
    import { algorithmIndex, digestIndex, ready as dnssecDataReady } from 'wdom/services/dnssec'

    import { wbr } from 'hydria/utils/strings'

    import type { DomainHandler } from './domain'
    import type { IDomain, IDSRecord } from 'wdom/domain/interfaces'

    import Date from 'hydria/components/Date.svelte'

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    interface Props {
        editDsRecords: () => void
    }

    let { editDsRecords }: Props = $props()

    const Domain = <DomainHandler>getContext('Domain')

    const status = sderived(Domain.status, ($status) => $status)

    let domain: IDomain = $derived($status == 'ready' ? Domain.domain : <IDomain>{})

    let dsTaskRunning = $state(false)

    unsubs.push(
        Domain.runningTasks.dsRecords.subscribe(($running) => {
            dsTaskRunning = $running
        }),
    )

    let dsRecords: IDSRecord[] = $derived.by(() => {
        dsTaskRunning
        if ($status == 'ready') return domain.ds_records
        return []
    })

    let linked: boolean = $derived.by(() => {
        newLinkValue
        return $status == 'ready' ? Domain.zoneDomainRelation.ds_domain_linked : false
    })

    let newLinkValue: boolean | null = $state(null)
    let linking = $state(false)

    async function setLinkedToZone() {
        linking = true
        const res = await Domain.setDsRecordsLinkedToZone(!!newLinkValue)
        linking = false
        newLinkValue = null
    }

    $effect(() => {
        if (newLinkValue === Domain.zoneDomainRelation.ds_domain_linked) newLinkValue = null
    })

    $effect(() => {
        if (newLinkValue && !document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if (!newLinkValue && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
</script>

{#if $dnssecDataReady && $status == 'ready'}
    <div class="card">
        <div class="card-title text-primary">DNSSEC</div>
        <div class="card-body">
            <div class="table-responsive ds-record-table">
                <table class="table table-condensed table-striped fixed-no-overflow">
                    <thead>
                        <tr>
                            <th class="ctime">Date</th>
                            <th class="key-tag">Key tag</th>
                            <th class="alg">Algorithm</th>
                            <th class="digest-type">Digest type</th>
                            <th class="digest">Digest</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#if !dsRecords.length}
                            <tr><th colspan="5">{$_('No DS records')}</th></tr>
                        {:else}
                            {#each dsRecords as dsr}
                                <tr>
                                    <td class="ctime"><Date value={dsr.ctime} format="h-table-s" /></td>
                                    <td class="key-tag">{dsr.key_tag}</td>
                                    <td class="alg">{dsr.alg} – {$algorithmIndex[dsr.alg].text}</td>
                                    <td class="digest-type">{dsr.digest_type} – {$digestIndex[dsr.digest_type].name}</td>
                                    <td class="digest">{@html wbr(dsr.digest, [32, 20])}</td>
                                </tr>
                            {/each}
                        {/if}
                    </tbody>
                </table>
            </div>
        </div>
        <div class="card-footer text-bg-light text-end">
            <div class="btn-toolbar gap-2">
                {#if !dsTaskRunning && !linked}
                    <div class="btn-group"><button type="button" class="btn btn-primary" onclick={() => editDsRecords()}>{$_('Edit DS records')}</button></div>
                    {#if Domain.zoneDomainRelation.zone_published}
                        <div class="btn-group"><button type="button" class="btn btn-warning" onclick={() => (newLinkValue = true)}>{$_('Activate automatic synchronization')}</button></div>
                    {/if}
                {:else if !dsTaskRunning}
                    <div class="btn-group"><button type="button" class="btn btn-warning" onclick={() => (newLinkValue = false)}>{$_('Deactivate automatic synchronization')}</button></div>
                {:else}
                    <div class="btn-group">
                        <button type="button" class="btn btn-outline-secondary"
                            ><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                            {$_('Update in progress')}</button
                        >
                    </div>
                {/if}
            </div>
        </div>
    </div>
{/if}

{#if newLinkValue !== null}
    <div class="modal" role="dialog">
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header text-bg-primary">
                    <div>
                        {#if newLinkValue}{$_('Activate automatic synchronization of DS records')}
                        {:else}{$_('Deactivate automatic synchronization of DS records')}
                        {/if}
                    </div>
                </div>
                <div class="modal-body">
                    {#if newLinkValue}
                        {$_('If your zone is hosted here on WebDomaine, activating this setting will ensure that your domain will keep the proper DS records at all times.')}
                    {:else}
                        {$_('Upon deactivation, it will become your responsability to ensure the DS records are copied to the domain upon key rotation.')}
                    {/if}
                </div>
                <div class="modal-footer">
                    <button class="btn btn-warning" type="button" disabled={linking} onclick={() => setLinkedToZone()}>
                        {#if linking}
                            <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                        {/if}
                        {#if newLinkValue}
                            {$_('Enable automatic synchronization')}
                        {:else}
                            {$_('Disable automatic synchronization')}
                        {/if}
                    </button>
                    <button class="btn btn-outline-secondary" type="button" disabled={linking} onclick={() => (newLinkValue = null)}>{$_('Cancel')}</button>
                </div>
            </div>
        </div>
        <div
            class="modal-backdrop"
            onclick={() => {
                if (!linking) newLinkValue = null
            }}
            aria-hidden="true"
        ></div>
    </div>
{/if}
